import type ServiceApi from "../../api";
import {RevaApi} from "../api-constants";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import type {ApiExecutor} from "../executor";
import type {UserPasswordUpdateRequest} from "../payload-types/user-profile";
import {WebError} from "../web-error";

/**
 * #19351: PUT {@link RevaApi.PATH_USER_PASSWORD} — changes the logged-in user's password. The stored JWT
 * stays valid, so a successful call never has to trigger a logout.
 *
 * 422/503 are rethrown as the raw {@link WebError} instead of being converted to a generic `ErrorI18n`
 * (unlike the base class default), so the dialog can show account-management's own `error`/`error_de`
 * message (e.g. failed password policy) resp. the fixed "not reachable" text.
 */
export class UserPasswordUpdateApiInterface extends SimpleApiInterface<object> {
    constructor(private readonly payload: UserPasswordUpdateRequest) {
        super();
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl([RevaApi.PATH_USER_PASSWORD]);
    }

    protected buildPayload(): UserPasswordUpdateRequest {
        return this.payload;
    }

    protected executeInternal(executor: ApiExecutor, url: string, credentials: ApiCredentials): Promise<object> {
        return executor.fetchPUT(url, credentials, this.buildPayload());
    }

    protected override handleApiErrors(e: Error): object {
        if (e instanceof WebError && (e.status === 422 || e.status === 503)) {
            throw e;
        }
        return super.handleApiErrors(e);
    }
}
